import { RmaLrpCommodityCodes, RmaLrpTypeCodes } from '@harvestiq/constants';
import { lrpQuoteSchema } from '@harvestiq/usda/common';
import { z, zCalendarDate, zDecimalRequired } from '@harvestiq/zod';

export const getQuotesV2QuerySchema = z.object({
  salesEffectiveDate: zCalendarDate().openapi({
    example: '2025-06-13',
  }),
  commodityCode: z.nativeEnum(RmaLrpCommodityCodes).openapi({
    example: RmaLrpCommodityCodes.FEEDER_CATTLE,
  }),
  typeCode: z.nativeEnum(RmaLrpTypeCodes).openapi({
    example: RmaLrpTypeCodes.STEERS_WEIGHT_1,
  }),
  endDate: zCalendarDate().optional().openapi({
    example: '2025-09-12',
  }),
  endorsementLengthCount: z.coerce.number().int().optional().openapi({
    example: 13,
  }),
});

export type GetQuotesV2Query = z.infer<typeof getQuotesV2QuerySchema>;

export const getQuotesV2ResponseSchema = z.object({
  quotes: lrpQuoteSchema
    // Using pick to exclude unnecessary legacy fields
    .pick({
      commodityCode: true,
      typeCode: true,
      salesEffectiveDate: true,
      endDate: true,
      endorsementLengthCount: true,
      reinsuranceYear: true,
      targetLowWeight: true,
      targetHighWeight: true,
      barchartFuturesSymbol: true,
      savingsVsCmeTrade: true,
      livestockCoverageLevelPercent: true,
      livestockRate: true,
    })
    .extend({
      coveragePricePerCwt: zDecimalRequired(),
      producerPremiumPerCwt: zDecimalRequired(),
      subsidyPerCwt: zDecimalRequired(),
      expectedEndingValuePerCwt: zDecimalRequired(),
      cmePutPremium: z.number(),
      cmePutStrike: z.number().int(),
    })
    .array()
    .openapi({
      externalDocs: {
        description:
          'Commodity Codes and Type Codes are provided by the RMA docs and can vary by year.',
        url: 'https://pubfs-rma.fpac.usda.gov/pub/References/actuarial_data_master/2026/index.html',
      },
      example: [
        {
          commodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
          typeCode: RmaLrpTypeCodes.STEERS_WEIGHT_1,
          salesEffectiveDate: '2025-06-13',
          endDate: '2025-09-12',
          endorsementLengthCount: 13,
          reinsuranceYear: 2025,
          targetLowWeight: 11.5,
          targetHighWeight: 12.5,
          barchartFuturesSymbol: 'LEZ25',
          savingsVsCmeTrade: 0.05,
          livestockCoverageLevelPercent: 95,
          livestockRate: 1.25,
          coveragePricePerCwt: 150.0,
          producerPremiumPerCwt: 10.0,
          subsidyPerCwt: 2.5,
          expectedEndingValuePerCwt: 145.0,
          cmePutPremium: 0.75,
          cmePutStrike: 140,
        },
      ],
    }),
});
export type GetQuotesV2Response = z.infer<typeof getQuotesV2ResponseSchema>;
